/* eslint-disable import/no-anonymous-default-export */
// npm i pocketbase
// import PocketBase from 'pocketbase'

const escapeValue = (v) => {
  if (v === null) return 'null';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  // PocketBase filter-Strings müssen in Anführungszeichen; Anführungszeichen escapen
  return `"${String(v).replace(/"/g, '\\"')}"`;
};

// Baue PB-Filterstring aus react-admin params.filter
const buildPbFilter = (filter = {}) => {
  const parts = [];

  for (const [rawKey, rawVal] of Object.entries(filter)) {
    if (rawVal === undefined || rawVal === null || rawVal === '') continue;

    // Spezialfall: q => nach id suchen (wie im Firebase-Adapter)
    if (rawKey === 'q') {
      parts.push(`id=${escapeValue(rawVal)}`);
      continue;
    }

    // _lte / _gte / _unset erkennen
    let key = rawKey;
    let op = null;

    if (rawKey.endsWith('_lte')) { key = rawKey.slice(0, -4); op = '<='; }
    else if (rawKey.endsWith('_gte')) { key = rawKey.slice(0, -4); op = '>='; }
    else if (rawKey.endsWith('_unset')) { key = rawKey.slice(0, -6); op = 'unset'; }

    if (op === 'unset') {
      parts.push(`${key}=null`);
      continue;
    }

    // Datums- / Zahlenkonvertierung (react-admin kann Strings schicken)
    let val = rawVal;
    if (typeof val === 'string' && val.includes('-') && !isNaN(Date.parse(val))) {
      val = new Date(val).toISOString(); // PB speichert Date/DateTime als ISO
    }

    if (op) {
      parts.push(`${key} ${op} ${escapeValue(val)}`);
    } else {
      // Text enthält: PB "~" ist contains/LIKE
      // Für Nicht-Strings machen wir '='
      if (typeof val === 'string') {
        parts.push(`${key} ~ ${escapeValue(val)}`);
      } else {
        parts.push(`${key}=${escapeValue(val)}`);
      }
    }
  }

  return parts.join(' && ');
};

// Mappt PocketBase File-Felder zu react-admin {src,title,size,id}-Objekten (wie dein Firebase-Adapter)
const filesToRaArray = (pb, record, field) => {
  const files = record?.[field];
  if (!files) return [];
  const arr = Array.isArray(files) ? files : [files];
  return arr.map((name, i) => ({
    src: pb.files.getUrl(record, name), // erzeugt absolute URL
    title: name,
    id: `${record.id}_${field}_${i}`,
    // size unbekannt → leer; falls du Size in PB hältst, hier ergänzen
  }));
};

// Extrahiert Dateinamen von "bestehenden" Dateien aus react-admin Feldwerten
const existingFilenamesFromRa = (val) => {
  if (!val) return [];
  const arr = Array.isArray(val) ? val : [val];
  // Versuche aus Titel/URL den Dateinamen zu rekonstruieren
  return arr
    .filter(v => !v?.rawFile) // nur bestehende
    .map(v => {
      if (typeof v === 'string') return v;
      if (v?.title) return v.title;
      if (v?.src) {
        try {
          const u = new URL(v.src);
          const parts = u.pathname.split('/');
          return parts[parts.length - 1];
        } catch {
          return v.src;
        }
      }
      return '';
    })
    .filter(Boolean);
};

// Baut FormData für create/update (PocketBase: vorhandene Dateinamen + neue Files)
const buildFormData = (data, imageKey, fileKey) => {
  const fd = new FormData();

  // Alle nicht-Datei Felder rein (Arrays/Objekte → JSON)
  Object.entries(data).forEach(([k, v]) => {
    if (k === imageKey || k === fileKey) return; // separat behandeln
    if (v === undefined) return;
    if (v instanceof File || v?.rawFile instanceof File) return; // sicherheitshalber
    if (typeof v === 'object' && v !== null) {
      fd.append(k, JSON.stringify(v));
    } else {
      fd.append(k, v);
    }
  });

  // Helper: vorhandene behalten + neue hinzufügen
  const appendFiles = (field) => {
    const value = data[field];
    if (!value) return;

    const arr = Array.isArray(value) ? value : [value];

    // Bestehende Dateinamen sammeln
    const keep = existingFilenamesFromRa(arr);
    if (keep.length) {
      keep.forEach(n => fd.append(field, n)); // PB: gleicher Feldname mehrfach → Multi-File beibehalten
    }

    // Neue Files anhängen
    arr.forEach((item) => {
      const f = item?.rawFile instanceof File ? item.rawFile : (item instanceof File ? item : null);
      if (f) {
        fd.append(field, f, f.name);
      }
    });
  };

  if (imageKey) appendFiles(imageKey);
  if (fileKey) appendFiles(fileKey);

  return fd;
};

// Wandelt PB-Record zu react-admin Datensatz inkl. {images/files}-Objektlisten (kompatibel zu deinem Frontend)
const toRaRecord = (pb, rec, imageKey, fileKey) => {
  const base = { ...rec }; // PB-Record hat id, created, updated, etc.
  if (imageKey) {
    base[imageKey] = filesToRaArray(pb, rec, imageKey);
  }
  if (fileKey) {
    base[fileKey] = filesToRaArray(pb, rec, fileKey);
  }
  return base;
};

export default function pocketBaseDataProvider(pb, settings = { context: '', imagekey: 'images', filekey: 'files' }) {
  const ctx = settings?.context ?? '';
  const imageKey = settings?.imagekey ?? 'images';
  const fileKey = settings?.filekey ?? 'files';

  // optional: context als Präfix für Collectionnamen nutzen
  const coll = (source) => (ctx ? `${ctx}_${source}` : source);

  return {
    // CREATE
    create: async (source, params) => {
      const collection = coll(source);
      const formData = buildFormData({ ...params.data }, imageKey, fileKey);
      const record = await pb.collection(collection).create(formData);
      return { data: toRaRecord(pb, record, imageKey, fileKey) };
    },

    // UPDATE
    update: async (source, params) => {
      const collection = coll(source);
      const formData = buildFormData({ ...params.data }, imageKey, fileKey);
      const record = await pb.collection(collection).update(params.id, formData);
      return { data: toRaRecord(pb, record, imageKey, fileKey) };
    },

    // LIST (mit Sort, Pagination, Filter)
    getList: async (source, params) => {
      const collection = coll(source);
      const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
      const { field, order } = params.sort ?? { field: 'created', order: 'DESC' };

      const sort = `${order === 'DESC' ? '-' : ''}${field}`;
      const filter = buildPbFilter(params.filter || {});

      const res = await pb.collection(collection).getList(page, perPage, {
        sort,
        filter,
      });

      // res.items, res.totalItems
      const data = res.items.map((r) => toRaRecord(pb, r, imageKey, fileKey));
      const total = res.totalItems ?? data.length;

      return { data, total };
    },

    // ONE
    getOne: async (source, params) => {
      const collection = coll(source);
      const record = await pb.collection(collection).getOne(params.id);
      return { data: toRaRecord(pb, record, imageKey, fileKey) };
    },

    // MANY BY IDS
    getMany: async (source, params) => {
      const collection = coll(source);
      if (!params.ids?.length) return { data: [] };
      const filter = `id ~ ${escapeValue(`(${params.ids.join('|')})`)}`; // einfache IN-Emulation via Regex-ähnlich
      const list = await pb.collection(collection).getFullList({ filter, sort: '' });
      const mapped = list
        .filter(r => params.ids.includes(r.id))
        .map(r => toRaRecord(pb, r, imageKey, fileKey));
      return { data: mapped };
    },

    // MANY REFERENCE
    getManyReference: async (source, params) => {
      const collection = coll(source);
      const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
      const { field, order } = params.sort ?? { field: 'created', order: 'DESC' };
      const sort = `${order === 'DESC' ? '-' : ''}${field}`;

      // target-Feld muss in PB existieren (FK/Relation/Plain ID)
      const baseFilter = `${params.target}=${escapeValue(params.id)}`;
      const extra = buildPbFilter(params.filter || {});
      const filter = extra ? `${baseFilter} && (${extra})` : baseFilter;

      const res = await pb.collection(collection).getList(page, perPage, { sort, filter });
      const data = res.items.map(r => toRaRecord(pb, r, imageKey, fileKey));
      const total = res.totalItems ?? data.length;
      return { data, total };
    },

    // UPDATE MANY
    updateMany: async (source, params) => {
      const collection = coll(source);
      const results = await Promise.all(
        params.ids.map(id => pb.collection(collection).update(id, params.data))
      );
      return { data: results.map(r => r.id) };
    },

    // DELETE
    delete: async (source, params) => {
      const collection = coll(source);
      await pb.collection(collection).delete(params.id);
      return { data: params.previousData ?? { id: params.id } };
    },

    // DELETE MANY
    deleteMany: async (source, params) => {
      const collection = coll(source);
      await Promise.all(params.ids.map(id => pb.collection(collection).delete(id)));
      return { data: params.ids };
    },
  };
}
